<template>
  <div class="w-full">
    <div class="flex w-full flex-col items-center pb-8 lg:flex-row">
      <h3 class="text-display-small">{{ feedbackTopic.title }}</h3>
      <div class="flex flex-row items-center gap-2 lg:ml-auto">
        <div v-if="data" class="flex flex-row">
          <BaseMemberAvatars class="bg-surface-container-lowest" :members="data.feedbackSenders" />
        </div>
        <BaseTextButton
          v-if="allSurveys.length > 0"
          color="outlined"
          :size="{ base: 'S', lg: 'M' }"
          shape="square"
          icon="keyboard_arrow_down"
          @click="openDialog"
        >
          {{ inputData.surveyIds.length }} Umfragen ausgewählt</BaseTextButton
        >
        <div
          v-if="!hasChanged && slideProperties.updatedAt"
          class="bg-surface-container text-title-small lg:text-title-medium text-on-surface-variant flex h-10 flex-row items-center rounded-full px-6 lg:h-14"
        >
          <BaseIcon class="mr-2">check_circle</BaseIcon>
          Abgeschlossen am {{ useDisplayDate(slideProperties.updatedAt, { dayName: 'none' }) }}
        </div>
        <BaseTextButton
          v-if="hasChanged && slideProperties.updatedAt"
          color="filled"
          :size="{ base: 'S', lg: 'M' }"
          icon="cloud_upload"
          @click="updateSlide"
          >Änderungen Speichern</BaseTextButton
        >
        <BaseTextButton
          v-if="!slideProperties.updatedAt"
          color="filled"
          :size="{ base: 'S', lg: 'M' }"
          icon="check_circle"
          @click="updateSlide"
          >Thema abschliessen</BaseTextButton
        >
      </div>
    </div>

    <SurveySelectorDialog
      :subject="user"
      title="Umfragen auswählen"
      :model-value="inputData.surveyIds"
      :is-open="isOpen"
      :all-surveys="allSurveys"
      :topic-perspectives="topicPerspectives"
      @close-dialog="closeDialog"
      @update:model-value="selectSurveys"
    />

    <div v-if="allSurveys.length > 0">
      <LoadingWrapper :state="status">
        <div class="mb-2 flex w-full justify-end pr-1">
          <PerspectiveLegend :all-perspectives="topicPerspectives" />
        </div>
        <div class="grid w-full grid-cols-1 items-start gap-4 md:grid-cols-12">
          <div
            class="col-span-1 flex shrink-0 grow-0 flex-col gap-0.5 md:col-span-6 2xl:col-span-4"
          >
            <div class="bg-surface-container rounded-large p-4 md:p-8">
              <MenonChart
                :skill-analysis="allSkills"
                :max-value="scale.items.length"
                :topic-perspectives="topicPerspectives"
              />
            </div>
          </div>
          <div class="col-span-1 min-w-0 grow md:col-span-6 2xl:col-span-8">
            <SelectQuestionsForEvaluationAnalysis
              :questions="data.questions"
              :scale="scale.items.map((item) => ({ value: item.value, label: item.label! }))"
              :feedback-topic-id="feedbackTopic.id"
              :topic-perspectives="topicPerspectives"
              :input-data="inputData"
              :include-blind-spots="
                feedbackTopic.includeSelf && feedbackTopic.perspectives.length > 0
              "
            />
            <SelectTextsForEvaluationAnalysis
              class="mt-4"
              :texts="data.texts"
              v-model="inputData.textIds"
            />
          </div>
        </div>
      </LoadingWrapper>
    </div>
    <div v-else class="mx-auto flex max-w-lg flex-col items-center justify-center p-4">
      <EmptyQuestions />
      <h3 class="text-headline-medium text-center">Du hast noch kein Feedback zu diesem Thema</h3>
      <p class="text-body-large text-on-surface-variant mt-2 text-center">
        Starte selbst eine Umfrage, gib eine Selbsteinschätzung ab oder warte auf Rückmeldungen von
        anderen.
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, watch } from 'vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import SurveySelectorDialog from '../../../core/components/survey-selector/SurveySelectorDialog.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useEvaluationFeedbackTopicSlideAnalysis } from '../composable/use-evaluation-feedback-topic-slide-analysis'
  import LoadingWrapper from '../../../core/components/loading/LoadingWrapper.vue'
  import MenonChart from '../../../core/components/analytics/menon-chart/MenonChart.vue'
  import type { Skill } from '../../../core/models/skill'
  import type { FeedbackTopic } from '../../../core/models/feedback-topic'
  import BaseMemberAvatars from '../../../core/components/base/avatar/BaseMemberAvatars.vue'
  import SelectQuestionsForEvaluationAnalysis from './SelectQuestionsForEvaluationAnalysis.vue'
  import type { Scale } from '../../../core/models/scale'
  import { useDisplayDate } from '../../../core/composables/use-display-date'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import type { SurveySelectionPreview } from '@/modules/profile/composables/use-evaluation-profile'
  import { useSkillAnalysis } from '@/core/composables/use-skill-analysis'
  import EmptyQuestions from '@/core/components/analytics/question-list/EmptyQuestions.vue'
  import SelectTextsForEvaluationAnalysis from './SelectTextsForEvaluationAnalysis.vue'
  import { useUserStore } from '@/stores/user.store'
  import { perspectivesFromTopic } from '@/core/models/perspective'
  import PerspectiveLegend from '@/core/components/analytics/question-list/PerspectiveLegend.vue'

  interface Props {
    feedbackTopic: FeedbackTopic & { skills: ReadonlyArray<Skill> }
    allSurveys: ReadonlyArray<SurveySelectionPreview>
    scale: Scale
    slideProperties: {
      updatedAt: string | null
      surveyIds: Array<string>
      overestimateIds: Array<string>
      potentialIds: Array<string>
      strengthIds: Array<string>
      underestimateIds: Array<string>
      textIds: Array<string>
    }
  }
  const props = defineProps<Props>()
  const { isOpen, closeDialog, openDialog } = useDialog()
  const { user } = useUserStore()
  const emit = defineEmits<{
    (e: 'dirty', value: boolean): void
  }>()

  const topicPerspectives = computed(() => perspectivesFromTopic(props.feedbackTopic))

  const selectSurveys = (surveyIds: string[]) => {
    closeDialog()
    inputData.surveyIds = surveyIds
    reload({ query: { surveyIds: inputData.surveyIds } })
  }

  const { data, status, reload, updateSlide, inputData, hasChanged } =
    useEvaluationFeedbackTopicSlideAnalysis(
      props.feedbackTopic.id,
      props.allSurveys.map((survey) => survey.id),
      props.slideProperties,
    )

  watch(hasChanged, (value) => {
    emit('dirty', value)
  })

  const skills = computed(() => data.value?.skills ?? [])
  const { allSkills } = useSkillAnalysis(skills)
</script>
